import React from "react";

import { FrameViews } from "@com.mgmtp.a12.client/client-core/lib/core/frame";
import { View } from "@com.mgmtp.a12.client/client-core/lib/core/view";
import { NavigationContentboxContext } from "@com.mgmtp.a12.widgets/widgets-core/lib/contentbox/main/action-contentbox/action-contentbox.view";
import { VisibleView } from "@com.mgmtp.a12.widgets/widgets-core/lib/layout/master-detail/main/master-detail.api";
import { MasterDetail } from "@com.mgmtp.a12.widgets/widgets-core/lib/layout/master-detail/main/master-detail.view";
import { SizeContext } from "@com.mgmtp.a12.widgets/widgets-core/lib/layout/size-detector/main/size-context";

import { CHANGELOG_ACTIVITY } from "../../modules/changelog/constants";

import { MobileNavigationContext } from "../mobileNavigationContext";

/**
 * This activities views will show additionally to the main views.
 */
const EXTRA_VIEW_ACTIVITY_IDS = [CHANGELOG_ACTIVITY];

/**
 * Eigenes Layout zur Anzeige von 1-2 Views (e.g. Antrag links und Genehmigungsschritt rechts).
 *
 * In A12 werden die 3 Größen , "sm" (default > 768px), "md" (default > 992px) and "lg" (default > 1200px) unterschieden.
 *
 * Das Layout zeigt auf einem großen Viewport (md und lg) zwei Views nebeneinander an.
 * Im Unterschied zum Default MasterDetail, wird hier ein einzelner View nur auf der halbe Breite angezeigt.
 *
 * Auf einem kleinen Viewport (sm) wird immer nur ein View angezeigt.
 * Gibt es mehr als einen View, wird ein Back-Button und ein Forwar-Button angezeigt,
 * um zwischen den Views hin und her zu springen.
 *
 * Dokumentation getA12:
 * 	Client Keywords:
 * 		Custom Layout (Features > Layouts > Custom Layouts)
 * 		Mobile Navigation (Features > Mobile Navigation)
 */
export const ProcessLayout: FrameViews.Layout = {
	component: function ProcessLayout(props: FrameViews.LayoutProps) {
		/** Context um die aktuelle Größe des Viewports zu bekommen */
		const currentSize = React.useContext(SizeContext).currentSize;

		/**
		 * Context über den die Parent Component den Kindern mitteilt,
		 * welche Funktionalität auf dem Back-Button liegen soll
		 */
		const navigationContentbox = React.useContext(NavigationContentboxContext);

		/**
		 * Index des Views welcher gerade angezeigt wird, wenn der Viewport die Größe
		 * "sm" hat (da hier immer nur ein View angezeigt wird)
		 */
		const [currentViewIndex, setCurrentViewIndex] = React.useState<number>(0);

		const mobileSize = ["sm", "xs"].includes(currentSize ?? "");

		const views = props.views;

		React.useEffect(() => {
			/**
			 * Wenn die Fenstergröße geändert wird und wir vor einer großen auf eine kleine Größe
			 * ändern, müssen wir den aktuell angezeigten View Index setzen. Der Index richtet sich danach
			 * ob wir 1 oder 2 Views haben.
			 */
			if (mobileSize) {
				setCurrentViewIndex(Math.max(views.length - 1, 0));
			}
		}, [views.length, mobileSize]);

		const createVisibleView = (view: View, index: number): VisibleView => {
			const { name, activityId, ...rest } = view;

			const ViewComponent = props.viewProvider(name);
			const ProgressComponent = props.progressComponentProvider(props.regionReference, name);

			/**
			 * Das Default MobileNavigation Verhalten geht nur davon aus, dass man
			 * einen Back-Button hat, dessen Verhalten man überschreiben kann.
			 * Wir brauchen für unseren Use-Case aber auch einen Forward-Button,
			 * weswegen wir unseren eigenen Context (MobileNavigationContext)
			 * benutzen und dort neben dem 'onBackButtonClicked' callback
			 * auch einen 'onForwardButtonClicked' anbieten.
			 * Unsere Form-Engine wird sich dann um das rendern dieser Button
			 * kümmern.
			 */
			const Content = (
				<div style={{ width: mobileSize || views.length > 1 ? "100%" : "50%", height: "100%" }}>
					<MobileNavigationContext.Provider
						value={{
							onBackButtonClicked:
								/**
								 * Setze den aktuellen View Index um 1 zurück,
								 * wenn der aktuelle Index nicht 0 ist und wir
								 * uns auf einem kleinen Viewport befinden.
								 *
								 * Wenn dies nicht der Fall ist, benutze
								 * den default 'onBackButtonClicked' callback.
								 */
								mobileSize && currentViewIndex !== 0
									? () => {
											setCurrentViewIndex(currentViewIndex - 1);
										}
									: navigationContentbox.onBackButtonClicked,
							onForwardButtonClicked:
								/**
								 * Setze den aktuellen View Index um 1 nach vorne,
								 * wenn der aktuelle Index nicht der letzte ist ist und wir
								 * uns auf einem kleinen Viewport befinden.
								 */
								mobileSize && currentViewIndex < views.length - 1
									? () => {
											setCurrentViewIndex(currentViewIndex + 1);
										}
									: undefined
						}}
					>
						<ProgressComponent activityId={activityId}>
							<ViewComponent name={name} activityId={activityId} ariaLevel={index + 1} {...rest} />
						</ProgressComponent>
					</MobileNavigationContext.Provider>
				</div>
			);

			return {
				element: Content,
				key: `${index}-${name}`,
				width: 6
			};
		};

		/**
		 * Erstellen einer Liste der aktuellen sichtbaren Views,
		 * abhängig davon welche Größe der Viewport hat.
		 */
		const visibleViews: Record<string, VisibleView> = {};
		if (!mobileSize) {
			visibleViews[views[0].activityId] = createVisibleView(views[0], 0);
			const mainViews = views.filter(e => !EXTRA_VIEW_ACTIVITY_IDS.includes(e.activityId));
			if (mainViews.length > 1) {
				visibleViews[mainViews[mainViews.length - 1].activityId] = createVisibleView(
					mainViews[mainViews.length - 1],
					mainViews.length - 1
				);
			}
			EXTRA_VIEW_ACTIVITY_IDS.forEach(activityId => {
				const viewIndex = views.findIndex(e => e.activityId === activityId);
				if (viewIndex !== -1 && !(activityId in visibleViews)) {
					visibleViews[views[viewIndex].activityId] = createVisibleView(views[viewIndex], viewIndex);
				}
			});
		} else if (views.length - 1 >= currentViewIndex) {
			visibleViews[views[currentViewIndex].activityId] = createVisibleView(
				views[currentViewIndex],
				currentViewIndex
			);
		}

		return <MasterDetail visibleViews={Object.values(visibleViews)} animation={{ enabled: false }} />;
	}
};
